import React, { useMemo, useState } from "react";
import {
    Box,
    Chip,
    CircularProgress,
    Grid,
    Stack,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    Typography,
} from "@mui/material";
import ReactEcharts from "echarts-for-react";
import moment from "moment";
import Section from "../../../../common/components/Dashboard/Section";
import { useDashboardTrackingHistory } from "../../../../common/API/hooks";
import { useSelectorValue } from "../../../customDashboards/common/providers/selectorProvider";
import { formatDateToObjectFromAndTo } from "../utils";

const CHART_PAGE_SIZE = 2000;

// Office hours: 8 AM – 7 PM
const OFFICE_START = 8;
const OFFICE_END = 19;

const TIME_BANDS = [
    { label: "Before Office Hours", short: "Pre-Office",   start: 0,           end: OFFICE_START,  color: "#9c27b0" },
    { label: "Start of Day",        short: "Start of Day", start: OFFICE_START, end: 10,            color: "#42a5f5" },
    { label: "Core Hours",          short: "Core Hours",   start: 10,          end: 15,            color: "#66bb6a" },
    { label: "End of Day",          short: "End of Day",   start: 15,          end: OFFICE_END,    color: "#ffa726" },
    { label: "After Hours",         short: "After Hours",  start: OFFICE_END,  end: 24,            color: "#ef5350" },
];

const DAYS_LABEL = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const HOURS_LABEL = Array.from({ length: 24 }, (_, i) => `${String(i).padStart(2, "0")}:00`);

function getTimeBand(hour) {
    return TIME_BANDS.find((b) => hour >= b.start && hour < b.end) || TIME_BANDS[0];
}

export default function BrowsingPattern({ username }) {
    const date = useSelectorValue("date");
    const dateTo = useSelectorValue("dateTo");
    const module = useSelectorValue("module");
    const dateRange = formatDateToObjectFromAndTo(date, dateTo);

    const [view, setView] = useState("overall");

    const filters = useMemo(
        () => ({
            ...dateRange,
            username: username || null,
            module_name: module === "All" ? null : module,
            page: 1,
            page_size: CHART_PAGE_SIZE,
            sort_by: "datetime",
            sort_order: "asc",
        }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [JSON.stringify(dateRange), username, module]
    );

    const { data, isLoading, error } = useDashboardTrackingHistory(filters);
    const results = data?.results ?? [];

    /* ── aggregated data ─────────────────────────────────────────── */

    // hourly totals [0..23]
    const hourlyData = useMemo(() => {
        const counts = Array(24).fill(0);
        results.forEach((item) => counts[moment(item.datetime).hour()]++);
        return counts;
    }, [results]);

    // day-of-week × hour matrix  matrix[day=0..6][hour=0..23]
    const heatmapPoints = useMemo(() => {
        const matrix = Array.from({ length: 7 }, () => Array(24).fill(0));
        results.forEach((item) => {
            const m = moment(item.datetime);
            matrix[m.day()][m.hour()]++;
        });
        const points = [];
        for (let day = 0; day < 7; day++) {
            for (let hour = 0; hour < 24; hour++) {
                points.push([hour, day, matrix[day][hour]]);
            }
        }
        return points;
    }, [results]);

    // per-day data: date → { hourly: [24] }
    const perDayData = useMemo(() => {
        const byDate = {};
        results.forEach((item) => {
            const m = moment(item.datetime);
            const dateStr = m.format("YYYY-MM-DD");
            if (!byDate[dateStr]) byDate[dateStr] = Array(24).fill(0);
            byDate[dateStr][m.hour()]++;
        });
        return byDate;
    }, [results]);

    const sortedDates = useMemo(() => Object.keys(perDayData).sort(), [perDayData]);

    // band statistics
    const bandStats = useMemo(() => {
        return TIME_BANDS.map((b) => ({
            ...b,
            count: hourlyData.slice(b.start, b.end).reduce((a, c) => a + c, 0),
        }));
    }, [hourlyData]);

    const total = results.length;
    const peakHour = total > 0 ? hourlyData.indexOf(Math.max(...hourlyData)) : null;
    const outsideOffice = hourlyData
        .slice(0, OFFICE_START)
        .concat(hourlyData.slice(OFFICE_END))
        .reduce((a, c) => a + c, 0);

    /* ── chart options ───────────────────────────────────────────── */

    const officeMarkArea = {
        silent: true,
        data: [
            [
                {
                    xAxis: HOURS_LABEL[OFFICE_START],
                    itemStyle: { color: "rgba(102,187,106,0.08)" },
                },
                { xAxis: HOURS_LABEL[OFFICE_END - 1] },
            ],
        ],
        label: {
            show: true,
            position: "insideTop",
            formatter: "Office Hours",
            color: "#66bb6a",
            fontSize: 10,
        },
    };

    const hourlyOption = {
        tooltip: {
            trigger: "axis",
            formatter: (params) => {
                const p = params[0];
                const h = p.dataIndex;
                const band = getTimeBand(h);
                return `<strong>${HOURS_LABEL[h]}</strong> — ${band.short}<br/>Visits: <strong>${p.value}</strong>`;
            },
        },
        grid: { containLabel: true, top: "18%", left: "3%", right: "3%", bottom: "8%" },
        xAxis: {
            type: "category",
            data: HOURS_LABEL,
            axisLabel: { rotate: 45, fontSize: 9 },
        },
        yAxis: { type: "value", minInterval: 1, name: "Visits" },
        visualMap: {
            show: false,
            type: "piecewise",
            seriesIndex: 0,
            dimension: 0,
            pieces: TIME_BANDS.map((b) => ({
                gte: b.start,
                lt: b.end,
                color: b.color,
            })),
        },
        series: [
            {
                type: "bar",
                data: hourlyData,
                barMaxWidth: 28,
                markArea: officeMarkArea,
            },
        ],
    };

    const heatmapMax = Math.max(...heatmapPoints.map((d) => d[2]), 1);
    const heatmapOption = {
        tooltip: {
            formatter: (p) => {
                const [hour, day, count] = p.data;
                const band = getTimeBand(hour);
                return `<strong>${DAYS_LABEL[day]} ${HOURS_LABEL[hour]}</strong> (${band.short})<br/>Visits: <strong>${count}</strong>`;
            },
        },
        grid: { containLabel: true, top: 30, left: "5%", right: "5%", bottom: "14%" },
        xAxis: {
            type: "category",
            data: HOURS_LABEL,
            splitArea: { show: true },
            axisLabel: { rotate: 45, fontSize: 9 },
        },
        yAxis: {
            type: "category",
            data: DAYS_LABEL,
            splitArea: { show: true },
        },
        visualMap: {
            min: 0,
            max: heatmapMax,
            calculable: true,
            orient: "horizontal",
            left: "center",
            bottom: "0%",
            inRange: { color: ["#f5f5f5", "#1976d2"] },
        },
        series: [
            {
                type: "heatmap",
                data: heatmapPoints,
                label: { show: heatmapMax <= 15, fontSize: 9 },
                emphasis: { itemStyle: { shadowBlur: 10, shadowColor: "rgba(0,0,0,0.5)" } },
            },
        ],
    };

    // Per-day stacked bar: one series per time band, x-axis = date
    const perDayOption = useMemo(() => {
        const bandSeriesData = TIME_BANDS.map((b) => ({
            name: b.short,
            type: "bar",
            stack: "total",
            itemStyle: { color: b.color },
            data: sortedDates.map((d) => {
                const hourly = perDayData[d] || Array(24).fill(0);
                return hourly.slice(b.start, b.end).reduce((a, c) => a + c, 0);
            }),
        }));

        return {
            tooltip: {
                trigger: "axis",
                axisPointer: { type: "shadow" },
                formatter: (params) => {
                    const dateStr = params[0]?.axisValue;
                    const dow = dateStr ? moment(dateStr).format("ddd") : "";
                    let lines = `<strong>${dateStr}</strong> (${dow})<br/>`;
                    params.forEach((p) => {
                        if (p.value > 0)
                            lines += `<span style="color:${p.color}">●</span> ${p.seriesName}: <strong>${p.value}</strong><br/>`;
                    });
                    return lines;
                },
            },
            legend: { bottom: 0, textStyle: { fontSize: 11 } },
            grid: { containLabel: true, top: "8%", left: "3%", right: "3%", bottom: "14%" },
            xAxis: {
                type: "category",
                data: sortedDates,
                axisLabel: { rotate: sortedDates.length > 14 ? 30 : 0, fontSize: 10 },
            },
            yAxis: { type: "value", minInterval: 1, name: "Visits" },
            series: bandSeriesData,
        };
    }, [sortedDates, perDayData]);

    /* ── render ──────────────────────────────────────────────────── */

    const toggle = (
        <ToggleButtonGroup
            value={view}
            exclusive
            onChange={(_, val) => val && setView(val)}
            size="small"
            sx={{ mr: 1 }}
        >
            <ToggleButton value="overall" sx={{ textTransform: "none", fontSize: "0.75rem" }}>
                Overall
            </ToggleButton>
            <ToggleButton value="heatmap" sx={{ textTransform: "none", fontSize: "0.75rem" }}>
                Heatmap
            </ToggleButton>
            <ToggleButton value="perday" sx={{ textTransform: "none", fontSize: "0.75rem" }}>
                Per Day
            </ToggleButton>
        </ToggleButtonGroup>
    );

    const renderContent = () => {
        if (error)
            return (
                <Typography color="error" p={2}>
                    Error loading data.
                </Typography>
            );

        if (isLoading)
            return (
                <Box display="flex" justifyContent="center" alignItems="center" height={150}>
                    <CircularProgress size={28} />
                </Box>
            );

        if (results.length === 0)
            return (
                <Typography variant="body2" color="text.secondary" p={2}>
                    No data available for the selected filters.
                </Typography>
            );

        return (
            <Box>
                {/* Summary strip */}
                <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mb: 2 }}>
                    <Chip label={`${total} total visits`} variant="outlined" size="small" />
                    {peakHour !== null && (
                        <Chip
                            label={`Peak: ${HOURS_LABEL[peakHour]} (${hourlyData[peakHour]} visits)`}
                            color="primary"
                            variant="outlined"
                            size="small"
                        />
                    )}
                    {bandStats.map(
                        (b) =>
                            b.count > 0 && (
                                <Tooltip key={b.label} title={b.label}>
                                    <Chip
                                        label={`${b.short}: ${b.count} (${Math.round((b.count / total) * 100)}%)`}
                                        size="small"
                                        variant="outlined"
                                        sx={{
                                            borderColor: b.color,
                                            color: b.color,
                                            backgroundColor: `${b.color}18`,
                                            fontWeight: 600,
                                        }}
                                    />
                                </Tooltip>
                            )
                    )}
                    {outsideOffice > 0 && (
                        <Chip
                            label={`Outside office hours: ${outsideOffice} (${Math.round((outsideOffice / total) * 100)}%)`}
                            size="small"
                            color="warning"
                            variant="outlined"
                        />
                    )}
                </Stack>

                {view === "overall" && (
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                Visits by Hour of Day
                            </Typography>
                            <ReactEcharts
                                option={hourlyOption}
                                notMerge
                                lazyUpdate
                                style={{ width: "100%", height: "240px" }}
                            />
                        </Grid>
                    </Grid>
                )}

                {view === "heatmap" && (
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                Activity Heatmap — Hour of Day × Day of Week
                            </Typography>
                            <ReactEcharts
                                option={heatmapOption}
                                notMerge
                                lazyUpdate
                                style={{ width: "100%", height: "260px" }}
                            />
                        </Grid>
                    </Grid>
                )}

                {view === "perday" && (
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                Visits per Day by Time Band
                            </Typography>
                            {sortedDates.length === 0 ? (
                                <Typography variant="body2" color="text.secondary">
                                    No per-day data.
                                </Typography>
                            ) : (
                                <ReactEcharts
                                    option={perDayOption}
                                    notMerge
                                    lazyUpdate
                                    style={{ width: "100%", height: "280px" }}
                                />
                            )}
                        </Grid>
                    </Grid>
                )}
            </Box>
        );
    };

    return (
        <Section title="Browsing Pattern" isCollapsible selectorsOrFilters={toggle}>
            {renderContent()}
        </Section>
    );
}

